import { isSubscriptionErr } from '../billing/shared';
// Static configuration for the Inspection & Delivery form.
//
// Kept in a single module so backend Pydantic models, frontend grid, and
// PDF generator can stay in sync visually (the codes are mirrored in
// /app/backend/inspection_pdf.py).

export const EXTERIOR_CODES = [
  ['SH', 'Swirls / Holograms'], ['WS', 'Water Spots'], ['OX', 'Oxidation'],
  ['CF', 'Clearcoat Failure'], ['DS', 'Deep Scratches'], ['BO', 'Bird Dropping Etching'],
  ['RP', 'Rough Paint'], ['UD', 'Unknown Defect'], ['PT', 'Paint Transfer'],
  ['PC', 'Paint Chip'], ['GS', 'Glass Scratch / Chip'], ['VC', 'Valve Caps'],
  ['DD', 'Dents / Dings'], ['SS', 'Side Swipe'], ['OD', 'Other Damage'],
  ['WD', 'Wheel Damage'], ['GC', 'Gouges / Crease'], ['LM', 'Loose Molding'],
  ['HR', 'Headlight Restoration'], ['EG', 'Engine Bay'],
];

export const INTERIOR_CODES = [
  ['LS', 'Light / Medium / Heavy Soil'], ['SI', 'Sunroof Issues'], ['FM', 'Floor Mats'],
  ['PM', 'Pet Hair'], ['TR', 'Tear'], ['LV', 'Leather / Vinyl Issues'],
  ['SB', 'Seat Belt Issues'], ['FI', 'Fabric Issues'], ['TS', 'Tea / Coffee Stain'],
  ['BS', 'Blood Stain'], ['OD', 'Odours'], ['TH', 'Trash'],
  ['OT', 'Other - Mirror, Grills etc.'], ['BT', 'Button Issues'], ['AC', 'AC Vent Issues'],
  ['DI', 'Display Issues'], ['RF', 'Rough Fabric'], ['RL', 'Rough Leather'],
  ['LI', 'Lighting Issues'],
];

export const FUEL_LABELS = ['E', '1/8', '1/4', '3/8', '1/2', '5/8', '3/4', '7/8', 'F'];

// Work-completion / handover checklist (Detailing/Ceramic/PPF/Wrap).
// Keys are mirrored in /app/backend/inspection_pdf.py — do not change without
// updating both.
export const COMPLETION_EXTERIOR = [
  ['logos', 'Logos'],
  ['beadings', 'Beadings'],
  ['glass_areas_top', 'Glass areas top'],
  ['mirror_edges', 'Mirror edges'],
  ['side_mirror_left', 'Side mirror left'],
  ['side_mirror_right', 'Side mirror right'],
  ['inside_mirror', 'Inside mirror'],
  ['front_grille', 'Front grille'],
  ['door_jambs', 'Door jambs'],
  ['number_plate_back', 'Number plate back side'],
  ['wheel_arches', 'Wheel arches'],
  ['cowl_panel', 'Cowl panel'],
  ['exhaust_tips', 'Exhaust tips'],
  ['front_glass_edges', 'Front glass edges'],
  ['wipers_rubber', 'Wipers rubber'],
  ['wheel_nuts', 'Wheel nuts'],
  ['calipers', 'Calipers'],
  ['door_handle_inside', 'Door handle inside area'],
  ['keyhole', 'Keyhole'],
  ['sunroof_area_sides', 'Sunroof area sides'],
  ['window_inner_beadings', 'Window inner beadings'],
  ['stepney_area_ext', 'Stepney area'],
  ['headlights_operation', 'Headlights operation'],
  ['taillights_operation', 'Taillights operation'],
  ['sunroof_glass_cleaning', 'Sunroof glass cleaning'],
  ['side_glass_cleaning', 'Side glass cleaning'],
  ['back_glass_cleaning', 'Back glass cleaning'],
  ['front_glass_cleaning', 'Front glass cleaning'],
];

export const COMPLETION_INTERIOR = [
  ['vacuum', 'Vacuum'],
  ['door_pads', 'Door pads'],
  ['dashboard', 'Dashboard'],
  ['seat_belts', 'Seat belts'],
  ['foot_mats', 'Foot Mats'],
  ['carpet', 'Carpet'],
  ['stepney_area_int', 'Stepney area'],
  ['seats', 'Seats'],
];

export const todayISO = () => new Date().toISOString().slice(0, 10);

export const formatErr = (e) => {
  if (isSubscriptionErr(e)) return '';
  const detail = e?.response?.data?.detail;
  if (!detail) return e?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(' ');
  return JSON.stringify(detail);
};

// See the billing version: a locked account already gets one global toast.
export const notifyErr = (e) => {
  const msg = formatErr(e);
  if (msg) alert(msg);
};

export const emptyForm = () => ({
  serial_no: '', inspection_date: todayISO(),
  customer_id: '',
  customer_name: '', contact_number: '', email: '', address: '',
  vehicle_year: '', make: '', model: '', registration_number: '', kms_run: '', fuel_level: 4, battery_charge_level: '',
  pickup_location: '', service_location: '', drop_location: '', estimated_delivery_time: '',
  receiver_name: '',
  exterior_issues: [], exterior_notes: {},
  interior_issues: [], interior_notes: {},
  remarks: '', estimate: '', executive_name_id: '',
  customer_observations: '',
  customer_signature: '', customer_printed_name: '', customer_approval_date: '',
  executive_signature: '',
  completion_checklist: {}, completion_notes: '', completion_date: '',
  checked_by_name: '', checked_by_signature: '',
  approved_by_name: '', approved_by_signature: '',
  status: 'draft',
});

export const inputCls =
  'w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 ' +
  'focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition';
